class PremiumPersonalizedProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.dataset.loaded === 'true') {
      this.handleLoadedState();
      return;
    }

    const recommendationsUrl = this.dataset.recommendationsUrl;
    if (!recommendationsUrl) {
      this.handleEmptyState();
      return;
    }

    this.abortController = new AbortController();
    this.setAttribute('aria-busy', 'true');

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer?.disconnect();
          this.loadRecommendations(recommendationsUrl);
        },
        { rootMargin: '500px 0px', threshold: 0.01 }
      );
      this.observer.observe(this);
      return;
    }

    this.loadRecommendations(recommendationsUrl);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController?.abort();
  }

  async loadRecommendations(recommendationsUrl) {
    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = fetch(recommendationsUrl, {
      signal: this.abortController?.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
        return response.text();
      })
      .then((responseText) => this.renderRecommendations(responseText))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.handleLoadingError();
      })
      .finally(() => {
        this.removeAttribute('aria-busy');
      });

    return this.loadingPromise;
  }

  renderRecommendations(responseText) {
    const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
    const escapedSectionId = CSS.escape(this.dataset.sectionId || '');
    const responseSection = responseDocument.querySelector(
      `premium-personalized-product-recommendations[data-section-id="${escapedSectionId}"]`
    );

    if (!responseSection) throw new Error('Recommendation section was missing from the response.');

    this.dataset.loaded = 'true';
    this.dataset.empty = responseSection.dataset.empty || 'false';

    if (this.shouldHideEmptySection()) {
      this.hidden = true;
      return;
    }

    const currentContent = this.querySelector(':scope > [data-recommendations-content]');
    const responseContent = responseSection.querySelector(':scope > [data-recommendations-content]');
    if (!currentContent || !responseContent) {
      throw new Error('Recommendation content was missing from the response.');
    }

    currentContent.replaceWith(document.importNode(responseContent, true));
    this.hidden = false;
    this.dispatchEvent(new CustomEvent('premium:recommendations:loaded', { bubbles: true }));
  }

  handleLoadedState() {
    this.removeAttribute('aria-busy');
    if (this.shouldHideEmptySection()) this.hidden = true;
  }

  handleEmptyState() {
    this.dataset.loaded = 'true';
    this.dataset.empty = 'true';
    this.removeAttribute('aria-busy');
    if (this.shouldHideEmptySection()) this.hidden = true;
  }

  handleLoadingError() {
    if (this.dataset.designMode === 'true') {
      this.dataset.loadError = 'true';
      this.hidden = false;
      return;
    }

    if (this.dataset.hideEmpty === 'true' && this.dataset.designMode !== 'true') {
      this.hidden = true;
      return;
    }

    const grid = this.querySelector('.ppc__grid');
    if (!grid) return;

    const message = document.createElement('p');
    message.className = 'ppr__empty';
    message.setAttribute('role', 'status');
    message.textContent = this.dataset.errorMessage || 'Recommendations could not be loaded.';
    grid.replaceChildren(message);
  }

  shouldHideEmptySection() {
    return (
      this.dataset.empty === 'true' &&
      this.dataset.hideEmpty === 'true' &&
      this.dataset.designMode !== 'true'
    );
  }
}

if (!customElements.get('premium-personalized-product-recommendations')) {
  customElements.define('premium-personalized-product-recommendations', PremiumPersonalizedProductRecommendations);
}
